import type { DueEntryT, ExtendedTrackerViewT } from "@patient-watch/types";
import { GenericChart } from "./components/GenericChart";
import { convertEntriesToChartConfig, convertEntriesToChartData } from "./components/utils";
import { isBmiForm } from "./bmiChart";
import { shouldShowTrackerChart } from "./TrackerChart";

type TrackerForChart = ExtendedTrackerViewT & {
    processed_schedule?: DueEntryT[];
};

const META_KEYS = new Set(["date", "timing", "timing_label", "patients"]);

interface TrackerSparkChartProps {
    tracker: TrackerForChart;
    className?: string;
    /** Palette slot 1-5; series colours start here and wrap through --chart-1…--chart-5. */
    colorIndex?: number;
}

function numericSeriesKeys(points: Array<Record<string, string | number | undefined>>, preferred: string[]): string[] {
    const present = new Set<string>();
    for (const point of points) {
        for (const [key, value] of Object.entries(point)) {
            if (!META_KEYS.has(key) && typeof value === "number" && !Number.isNaN(value)) {
                present.add(key);
            }
        }
    }
    const ordered = preferred.filter((key) => present.has(key));
    for (const key of present) {
        if (!ordered.includes(key)) ordered.push(key);
    }
    return ordered;
}

/** Mini tracker chart: GenericChart with the same series and scale, and no axes or legend. */
export function TrackerSparkChart({ tracker, className, colorIndex }: TrackerSparkChartProps) {
    const formId = tracker.form_id;
    if (!formId || !shouldShowTrackerChart(tracker)) return null;

    const chartType = (tracker.form?.chart_type ?? "line") as "line" | "stacked_area" | "bar";
    const chartConfig = convertEntriesToChartConfig(tracker.entries_view, formId);
    const chartData = convertEntriesToChartData(tracker.entries_view, formId, tracker.processed_schedule);
    const series = numericSeriesKeys(chartData, Object.keys(chartConfig));
    if (!series.length || !chartData.length) return null;

    const title = chartData
        .map((point) => {
            const label =
                typeof point.timing_label === "string" && point.timing_label ? point.timing_label : point.date;
            const scores = series
                .map((key) => (typeof point[key] === "number" ? `${key} ${point[key]}` : null))
                .filter(Boolean)
                .join(", ");
            return scores ? `${label}: ${scores}` : null;
        })
        .filter(Boolean)
        .join(" · ");

    return (
        <div className={className ?? "w-28 h-8 shrink-0"} title={title} data-testid="tracker-spark-chart">
            <GenericChart
                spark
                entries={tracker.entries_view}
                formId={formId}
                processedSchedule={tracker.processed_schedule}
                chartType={chartType}
                colorIndex={colorIndex}
                fillLineArea={!isBmiForm(formId)}
            />
        </div>
    );
}

export default TrackerSparkChart;
